import React, { useMemo, useState } from "react";
import type { Sport } from "../../../types/activity";
import OnboardingScreen, { OnboardingErrorText, OnboardingTitle } from "../OnboardingScreen";
import { OnboardingNextButton } from "../OnboardingButtons";
import { OnboardingCheckRow } from "../OnboardingRows";
import PeakTextField from "../PeakTextField";

interface SportsStepProps {
  sports: Sport[];
  selectedIDs: string[];
  loading: boolean;
  loadError: string | null;
  progress: number | null;
  onToggle: (id: string) => void;
  onBack: () => void;
  onContinue: () => void;
}

const SportsStep: React.FC<SportsStepProps> = ({ sports, selectedIDs, loading, loadError, progress, onToggle, onBack, onContinue }) => {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sports;
    return sports.filter((s) => s.name.toLowerCase().includes(q));
  }, [sports, query]);

  return (
    <OnboardingScreen
      progress={progress}
      onBack={onBack}
      layout="fixed"
      width="medium"
      footer={<OnboardingNextButton label="Continue" enabled={selectedIDs.length > 0} onClick={onContinue} />}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-7">
        <div className="shrink-0"><OnboardingTitle title="What sport do you do?" /></div>
        <div className="shrink-0">
          <PeakTextField label="Search" value={query} onChange={setQuery} placeholder="Search sports" autoCapitalize="none" autoComplete="off" />
        </div>
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-10">
            <span className="loading loading-spinner loading-md text-peak-text-secondary" aria-label="Loading sports" />
          </div>
        ) : loadError ? (
          <OnboardingErrorText>{loadError}</OnboardingErrorText>
        ) : (
          <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 gap-3 overflow-y-auto pb-1 lg:grid-cols-2" role="group" aria-label="Sports">
            {filtered.map((sport) => {
              const id = sport.id ?? sport.name;
              return <OnboardingCheckRow key={id} title={sport.name} selected={selectedIDs.includes(id)} onClick={() => onToggle(id)} />;
            })}
            {filtered.length === 0 && (
              <p className="font-funnel text-[14px] text-peak-text-secondary lg:col-span-2">No sports match “{query.trim()}”.</p>
            )}
          </div>
        )}
      </div>
    </OnboardingScreen>
  );
};

export default SportsStep;
